import type {
    ApiReviewAnalysis,
    ApiReviewAnalysisSentiment,
    ApiReviewAnalysisTopic,
} from '@storefronts/common-types'
import { ArrowDownRight, ArrowUpRight, Sparkles } from 'lucide-react'
import React, { useState } from 'react'
import { DEFAULT_LOCALE } from '../../../constants'
import {
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_BORDER_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../context/CustomizationContextProvider'
import { Constants } from '../../../style/styleConstants'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'
import { scaledFontSize } from '../../../style/scrollingLayout'

const SENTIMENT_POSITIVE_HEX = '#16a34a'
const SENTIMENT_NEGATIVE_HEX = '#ea580c'
const SENTIMENT_MIXED_HEX = '#6b7280'

const SENTIMENT_STYLES: Record<
    ApiReviewAnalysisSentiment,
    { color: string; Icon: typeof ArrowUpRight }
> = {
    POSITIVE: { color: SENTIMENT_POSITIVE_HEX, Icon: ArrowUpRight },
    MIXED: { color: SENTIMENT_MIXED_HEX, Icon: ArrowUpRight },
    NEGATIVE: { color: SENTIMENT_NEGATIVE_HEX, Icon: ArrowDownRight },
}

function formatMentionCount(n: number): string {
    if (n >= 1000) {
        const k = n / 1000
        const rounded = Math.round(k * 10) / 10
        return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}K`
    }
    return n.toLocaleString(DEFAULT_LOCALE)
}

function MixedSentimentIcon({ color }: { color: string }) {
    return (
        <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden
        >
            <path d="M4 12c2-4 6-4 8 0s6 4 8 0" />
        </svg>
    )
}

function SentimentGlyph({
    sentiment,
    color,
}: {
    sentiment: ApiReviewAnalysisSentiment
    color: string
}) {
    if (sentiment === 'MIXED') {
        return <MixedSentimentIcon color={color} />
    }
    const { Icon } = SENTIMENT_STYLES[sentiment]
    return <Icon size={20} strokeWidth={2.25} color={color} aria-hidden />
}

function renderExcerptWithBold(text: string, boldPhrase: string) {
    const phrase = boldPhrase.trim()
    if (!phrase) {
        return text
    }
    const idx = text.toLowerCase().indexOf(phrase.toLowerCase())
    if (idx === -1) {
        return text
    }
    const matchedInText = text.slice(idx, idx + phrase.length)
    return (
        <>
            {text.slice(0, idx)}
            <strong>{matchedInText}</strong>
            {text.slice(idx + phrase.length)}
        </>
    )
}

const TopicDetailCard: React.FC<{
    topic: ApiReviewAnalysisTopic
    textColor: string
}> = ({ topic, textColor }) => {
    const pos = topic.positiveCount
    const neg = topic.negativeCount

    return (
        <div
            style={{
                marginTop: 16,
                textAlign: 'left',
            }}
        >
            <p
                style={{
                    ...getTypographyRoleStyle('bodyText', {
                        lineHeight: 1.5,
                    }),
                    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.BODY),
                    color: textColor,
                    margin: '0 0 12px',
                }}
            >
                {topic.topicSummary}
            </p>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    ...getTypographyRoleStyle('captionText'),
                    fontSize: scaledFontSize(12),
                    color: textColor,
                    opacity: 0.65,
                    marginBottom: 16,
                }}
            >
                <Sparkles size={14} strokeWidth={2} aria-hidden />
                <span>Generated from the text of customer reviews</span>
            </div>
            <div
                style={{
                    ...getTypographyRoleStyle('featureTitle', {
                        fontWeight: 700,
                        lineHeight: 1.4,
                    }),
                    fontSize: scaledFontSize(16),
                    color: textColor,
                }}
            >
                {topic.mentionCount.toLocaleString(DEFAULT_LOCALE)} customers
                mention &quot;
                {topic.label}&quot;
            </div>
            <div
                style={{
                    marginTop: 6,
                    ...getTypographyRoleStyle('captionText', {
                        fontWeight: 400,
                    }),
                    fontSize: scaledFontSize(13),
                    marginBottom: 12,
                }}
            >
                <span style={{ color: SENTIMENT_POSITIVE_HEX }}>
                    {pos.toLocaleString(DEFAULT_LOCALE)} positive
                </span>
                <span
                    style={{
                        marginLeft: 8,
                        color: SENTIMENT_NEGATIVE_HEX,
                    }}
                >
                    {neg.toLocaleString(DEFAULT_LOCALE)} negative
                </span>
            </div>
            <ul
                style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                }}
            >
                {topic.excerpts.map((ex, i) => (
                    <li
                        key={i}
                        style={{
                            ...getTypographyRoleStyle('captionText', {
                                lineHeight: 1.5,
                            }),
                            fontSize: scaledFontSize(14),
                            color: textColor,
                            padding: '14px 0',
                            borderTop: `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`,
                        }}
                    >
                        &ldquo;{renderExcerptWithBold(ex.text, ex.boldPhrase)}
                        &rdquo;
                    </li>
                ))}
            </ul>
        </div>
    )
}

const AiCustomerReviewSummaryContent: React.FC<{
    analysis: ApiReviewAnalysis
}> = ({ analysis }) => {
    const textColor = `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
    const summary = analysis.summary?.trim()
    const topics = analysis.topics ?? []

    const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

    if (!summary && topics.length === 0) {
        return null
    }

    const selectedTopic =
        selectedIndex != null && selectedIndex < topics.length
            ? topics[selectedIndex]
            : null

    return (
        <div
            style={{
                width: '100%',
                maxWidth: 560,
                margin: '0 0 4px',
                textAlign: 'left',
            }}
        >
            <h2
                style={{
                    ...getTypographyRoleStyle('headingText', {
                        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                        fontWeight: 700,
                    }),
                    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.LABEL),
                    color: textColor,
                    margin: `0 0 ${Constants.SECTION_BASE_SPACING_PX}px`,
                }}
            >
                Customers say
            </h2>
            {summary && (
                <p
                    style={{
                        ...getTypographyRoleStyle('bodyText', {
                            lineHeight: 1.5,
                        }),
                        fontSize: scaledFontSize(TYPOGRAPHY_SIZE.BODY),
                        color: textColor,
                        margin: '0 0 12px',
                    }}
                >
                    {summary}
                </p>
            )}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    ...getTypographyRoleStyle('captionText'),
                    fontSize: scaledFontSize(12),
                    color: textColor,
                    opacity: 0.65,
                    marginBottom: topics.length > 0 ? 20 : 0,
                }}
            >
                <Sparkles size={14} strokeWidth={2} aria-hidden />
                <span>Generated from the text of customer reviews</span>
            </div>

            {topics.length > 0 && (
                <div>
                    <p
                        style={{
                            ...getTypographyRoleStyle('labelText', {
                                fontWeight: 700,
                            }),
                            fontSize: scaledFontSize(14),
                            color: textColor,
                            margin: '0 0 10px',
                        }}
                    >
                        Select to learn more
                    </p>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '4px 0',
                        }}
                    >
                        {topics.map((topic, index) => {
                            const { color } = SENTIMENT_STYLES[topic.sentiment]
                            const isLast = index === topics.length - 1
                            return (
                                <span
                                    key={`${topic.label}-${index}`}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setSelectedIndex((prev) =>
                                                prev === index ? null : index,
                                            )
                                        }
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 3,
                                            padding: 0,
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            ...getTypographyRoleStyle(
                                                'tabText',
                                                {
                                                    fontFamily: 'inherit',
                                                    fontWeight:
                                                        index === selectedIndex
                                                            ? 600
                                                            : 400,
                                                    lineHeight: 1.4,
                                                },
                                            ),
                                            fontSize: scaledFontSize(14),
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <SentimentGlyph
                                            sentiment={topic.sentiment}
                                            color={color}
                                        />
                                        <span style={{ color: textColor }}>
                                            {topic.label}
                                        </span>
                                        <span
                                            style={{
                                                color: textColor,
                                                opacity: 0.5,
                                                ...getTypographyRoleStyle(
                                                    'captionText',
                                                    { fontWeight: 400 },
                                                ),
                                                fontSize: scaledFontSize(12),
                                            }}
                                        >
                                            (
                                            {formatMentionCount(
                                                topic.mentionCount,
                                            )}
                                            )
                                        </span>
                                    </button>
                                    {!isLast && (
                                        <span
                                            style={{
                                                color: textColor,
                                                opacity: 0.2,
                                                marginLeft: 8,
                                                marginRight: 3,
                                                userSelect: 'none',
                                            }}
                                            aria-hidden
                                        >
                                            |
                                        </span>
                                    )}
                                </span>
                            )
                        })}
                    </div>
                    {selectedTopic && (
                        <TopicDetailCard
                            topic={selectedTopic}
                            textColor={textColor}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export const AiCustomerReviewSummary: React.FC<{
    analysis: ApiReviewAnalysis
    /** Resets topic selection when the shopper switches product / variant context. */
    linkHash: string
}> = ({ analysis, linkHash }) => {
    const analysisVersionKey = String(new Date(analysis.updatedAt).getTime())

    // Remount on context change so the selected topic resets.
    return (
        <AiCustomerReviewSummaryContent
            key={`${linkHash}:${analysisVersionKey}`}
            analysis={analysis}
        />
    )
}
